import moment from "moment";
import { listCalendarWeekPeriods } from "../../../../../../utils/block-review-helpers";
import { getWeekPeriodFromDate } from "../../../../../../utils/nutrition-helpers";

/**
 * Same calendar-week bucket key as Block Review (`YYYY-MM-DD - YYYY-MM-DD`).
 * @param {Object} exercise
 * @param {number} startingCalendarDay
 * @returns {string|null}
 */
export function periodKeyForExercise(exercise, startingCalendarDay) {
  if (exercise?.utcDate == null) {
    return null;
  }

  const { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
    String(exercise.utcDate),
    startingCalendarDay
  );

  return `${startOfWeek.format("YYYY-MM-DD")} - ${endOfWeek.format(
    "YYYY-MM-DD"
  )}`;
}

/**
 * Date-range views (Block Review, calendar month/week) → Program Insights inputs.
 * Weeks follow the athlete calendar (`startingCalendarDay`), not program week numbers.
 *
 * @param {Object} params
 * @param {Object[]} [params.exercises] — same set as the host table (e.g. Block Review rows)
 * @param {string} params.from — range start (`YYYY-MM-DD`)
 * @param {string} params.to — range end (`YYYY-MM-DD`)
 * @param {number} [params.startingCalendarDay]
 * @returns {{
 *   builderWeeks: Object<string, Object[]>,
 *   programExercises: Object[],
 *   weekHeaderLabels: Object<number, string>,
 * }|null}
 */
export function buildDateRangeInsightsInput({
  exercises = [],
  from,
  to,
  startingCalendarDay = 0,
}) {
  if (!from || !to) {
    return null;
  }

  const periods = listCalendarWeekPeriods(from, to, startingCalendarDay);
  if (periods.length === 0) {
    return null;
  }

  /** @type {Map<string, Object[]>} */
  const exercisesByPeriodKey = new Map(
    periods.map((period) => [period.periodKey, []])
  );

  /** @type {Map<string, Object>} */
  const exerciseById = new Map();

  for (const exercise of exercises) {
    if (exercise?._id == null) {
      continue;
    }

    exerciseById.set(String(exercise._id), exercise);

    const periodKey = periodKeyForExercise(exercise, startingCalendarDay);
    if (periodKey && exercisesByPeriodKey.has(periodKey)) {
      exercisesByPeriodKey.get(periodKey).push(exercise);
    }
  }

  /** @type {Object<string, Object[]>} */
  const builderWeeks = {};
  /** @type {Object<number, string>} */
  const weekHeaderLabels = {};

  periods.forEach((period, index) => {
    const weekIndex = index + 1;
    const weekExercises = exercisesByPeriodKey.get(period.periodKey) ?? [];

    // Group by training day so sessions.length = distinct days with ≥1 exercise.
    const exercisesByDay = new Map();
    for (const exercise of weekExercises) {
      const dayKey = moment.utc(String(exercise.utcDate)).format("YYYY-MM-DD");
      if (!exercisesByDay.has(dayKey)) {
        exercisesByDay.set(dayKey, []);
      }
      exercisesByDay.get(dayKey).push(exercise._id);
    }

    const sortedDays = Array.from(exercisesByDay.keys()).sort((a, b) =>
      a.localeCompare(b)
    );
    builderWeeks[String(weekIndex)] =
      sortedDays.length > 0
        ? sortedDays.map((dayKey, dayIdx) => ({
            week: weekIndex,
            order: dayIdx + 1,
            exercises: exercisesByDay.get(dayKey),
          }))
        : [];

    weekHeaderLabels[weekIndex] = period.label;
  });

  return {
    builderWeeks,
    programExercises: Array.from(exerciseById.values()),
    weekHeaderLabels,
  };
}

/**
 * Stable id for Program Insights sessionStorage (not a real cycle).
 * @param {string} scope — e.g. `block-review`
 * @param {string} athleteId
 * @param {string} from
 * @param {string} to
 */
export function dateRangeInsightsStorageId(scope, athleteId, from, to) {
  return `${scope}:${String(athleteId)}:${from}:${to}`;
}
